import { queryClient } from '@/router'
import { type CheckedState } from '@radix-ui/react-checkbox'
import { useMutation } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { toast } from 'sonner'

import { useClientPortalInternalData } from '@/pages/client-portal/internal/ui/hooks'

import { API_ENDPOINTS } from '@/lib/constants/api'
import { useSheet } from '@/lib/hooks/use-sheet'
import { type ClientPortalRelevantCaseItem } from '@/lib/queries/case-dashboard/types'
import { updateClientPortalOptionsMutation } from '@/lib/queries/case-dashboard/v1/portal/options/mutations'

import { type PortalControlSheetProps } from './portal-control/portal-control-sheet/portal-control-sheet'

export const useClientPortalInternalHeader = () => {
  const navigate = useNavigate()

  const {
    caseId: currentCaseId,
    caseNumber: currentCaseNumber,
    applicantId,
    companyId,
    dataAllApplicantCases,
    dataClientPortalInternal,
    isUscisCase,
  } = useClientPortalInternalData()

  const { mutateAsync: updateClientPortalOptions, isPending: isUpdatingClientPortalOptions } = useMutation(
    updateClientPortalOptionsMutation
  )

  const [isUpdatingCheckedCases, setIsUpdatingCheckedCases] = useState(false)
  const [beingUpdatedCaseId, setBeingUpdatedCaseId] = useState<number>()
  const {
    isSheetOpen: isOpenPortalControlSheet,
    openSheet: openPortalControlSheet,
    closeSheet: closePortalControlSheet,
  } = useSheet()

  const dataProUserSelectedCases = dataClientPortalInternal?.relevantCase
  const portalOptions = dataClientPortalInternal?.portalOption
  const copiedUrl = `${window.location.origin}/client-portal/public/${currentCaseId}?companyId=${companyId}`
  const indexOfCurrentCaseId = dataProUserSelectedCases?.findIndex(({ caseId }) => caseId === currentCaseId)

  const isDisabledAllCheckbox = isUpdatingCheckedCases
  const isLoadingPortalControlSaveButton = isUpdatingClientPortalOptions

  const handleClickPreviousButton = () => {
    if (indexOfCurrentCaseId === 0) {
      return
    }

    if (indexOfCurrentCaseId === undefined || !dataProUserSelectedCases) {
      return
    }

    const targetCaseId = dataProUserSelectedCases[indexOfCurrentCaseId - 1].caseId
    void navigate({
      to: '/client-portal/internal/$applicantId/$caseId',
      params: {
        applicantId,
        caseId: `${targetCaseId}`,
      },
    })
  }

  const setCheckedCaseUpdatingStates = (loading: boolean, caseId: number) => {
    setIsUpdatingCheckedCases(loading)
    setBeingUpdatedCaseId(loading ? caseId : undefined)
  }

  const handleClickCase = (caseItem: ClientPortalRelevantCaseItem) => {
    void navigate({
      to: '/client-portal/internal/$applicantId/$caseId',
      params: {
        applicantId,
        caseId: `${caseItem.caseId}`,
      },
    })
  }

  const handleClickNextButton = () => {
    if (dataProUserSelectedCases && indexOfCurrentCaseId === dataProUserSelectedCases?.length - 1) {
      return
    }

    if (!dataProUserSelectedCases || indexOfCurrentCaseId === undefined) {
      return
    }

    const targetCaseId = dataProUserSelectedCases[indexOfCurrentCaseId + 1].caseId

    void navigate({
      to: '/client-portal/internal/$applicantId/$caseId',
      params: {
        applicantId,
        caseId: `${targetCaseId}`,
      },
    })
  }

  const handleChangeCaseChecked = async (caseId: number, checked: CheckedState) => {
    setCheckedCaseUpdatingStates(true, caseId)
    await updateClientPortalOptions(
      {
        caseId: currentCaseId,
        otherCaseIds: dataProUserSelectedCases
          ? checked
            ? [...dataProUserSelectedCases.map(({ caseId }) => caseId), caseId]
            : dataProUserSelectedCases
                .filter(({ caseId: selectedCaseId }) => caseId !== selectedCaseId)
                .map(({ caseId }) => caseId)
          : checked
            ? [caseId]
            : [],
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: [API_ENDPOINTS['case-dashboard'].v1.portal.cases.index, currentCaseId],
          })
          setCheckedCaseUpdatingStates(false, caseId)
          closePortalControlSheet()
        },
        onError: () => {
          toast.error('Failed to update.')
          setCheckedCaseUpdatingStates(false, caseId)
        },
      }
    )
  }

  const handleClickPortalControlTriggerButton = () => {
    openPortalControlSheet()
  }

  const handleClickPortalControlSaveButton: PortalControlSheetProps['onClickSaveButton'] = async (data) => {
    await updateClientPortalOptions(
      { caseId: currentCaseId, ...data },
      {
        onSuccess: async () => {
          if (data.isDefaultSetting) {
            toast.success('The portal option setting has been successfully saved for all cases.', {
              duration: 5000,
            })
          } else {
            toast.success('The portal option setting has been successfully saved for this case only.', {
              duration: 5000,
            })
          }

          await queryClient.invalidateQueries({
            queryKey: [API_ENDPOINTS['case-dashboard'].v1.portal.cases.index, currentCaseId],
          })

          closePortalControlSheet()
        },
        onError: () => {
          toast.error('Failed to save the portal option setting.', {
            duration: 5000,
          })
        },
      }
    )
  }

  const handlePortalControlSheetOpen = (open: boolean) => {
    open ? openPortalControlSheet() : closePortalControlSheet()
  }

  return {
    currentCaseId,
    currentCaseNumber,
    copiedUrl,
    dataAllApplicantCases: dataAllApplicantCases?.filter(({ caseId }) => caseId !== currentCaseId),
    dataProUserSelectedCases,
    portalOptions,
    beingUpdatedCaseId,
    isOpenPortalControlSheet,
    isUscisCase,
    isDisabledAllCheckbox,
    isLoadingPortalControlSaveButton,
    handleClickPreviousButton,
    handleClickCase,
    handleClickNextButton,
    handleChangeCaseChecked,
    handleClickPortalControlTriggerButton,
    handleClickPortalControlSaveButton,
    handlePortalControlSheetOpen,
  }
}
